import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { platformService } from '@/services/platformService';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import MaintenanceModePage from '@/pages/MaintenanceModePage';
import { THEME_REGISTRY, DEFAULT_THEME, isThemeKey, type ThemeKey } from './registry';
import { assertThemeComplete } from './types';
import type { ThemePackage } from './types';

interface ThemeContextValue {
  themeKey: ThemeKey;
  components: ThemePackage;
}

// Same bypass set as ThemeProvider's own PREVIEW_ALLOWED_ROLES below, and matching the backend's
// PUT /v1/platform/maintenance-mode authorization (SUPER_ADMIN/PLATFORM_ADMIN) -- an admin who can
// turn maintenance mode off must still be able to reach the app while it's on.
const MAINTENANCE_BYPASS_ROLES = ['SUPER_ADMIN', 'PLATFORM_ADMIN'];

function canBypassMaintenance(): boolean {
  const user = useAuthStore.getState().user;
  const roles = user?.roles ?? (user?.role ? [user.role] : []);
  return roles.some((role) => MAINTENANCE_BYPASS_ROLES.includes(role));
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

const PREVIEW_ALLOWED_ROLES = ['SUPER_ADMIN', 'PLATFORM_ADMIN'];

function resolvePreviewOverride(): ThemeKey | null {
  const params = new URLSearchParams(window.location.search);
  const preview = params.get('previewTheme');
  if (!isThemeKey(preview)) return null;

  const user = useAuthStore.getState().user;
  const roles = user?.roles ?? (user?.role ? [user.role] : []);
  const canPreview = roles.some((role) => PREVIEW_ALLOWED_ROLES.includes(role));
  return canPreview ? preview : null;
}

async function loadTheme(themeKey: ThemeKey): Promise<ThemePackage> {
  const load = THEME_REGISTRY[themeKey] ?? THEME_REGISTRY[DEFAULT_THEME];
  const mod = await load();
  assertThemeComplete(themeKey, mod.default);
  return mod.default;
}

// Purely a perf optimization (avoids blocking first paint on platformService.getActiveTheme()'s
// round trip, which pays the full Lambda-cold-start tax on a low-traffic pre-launch app — see
// the 2026-07-20 load-time investigation) — never treated as authoritative. The live API call
// below always still runs and overwrites this once it resolves, so a stale/wrong cached value
// only affects the very first frame, never correctness.
const THEME_CACHE_KEY = 'bookbed:activeTheme';

function readCachedThemeKey(): ThemeKey | null {
  try {
    const cached = window.localStorage.getItem(THEME_CACHE_KEY);
    return isThemeKey(cached) ? cached : null;
  } catch {
    return null; // Storage unavailable (private browsing, quota) — just skip the optimization
  }
}

function writeCachedThemeKey(themeKey: ThemeKey): void {
  try {
    window.localStorage.setItem(THEME_CACHE_KEY, themeKey);
  } catch {
    // Storage unavailable — cache is a pure perf optimization, safe to skip
  }
}

/**
 * Resolves the platform's active structural theme before the router mounts, so there is no
 * flash of the wrong theme on first paint (this app is Vite CSR, no SSR — see ADR Section 16).
 * Falls back to DEFAULT_THEME if the platform setting is unreachable or a theme fails to load.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ThemeContextValue | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const preview = resolvePreviewOverride();
    const cachedThemeKey = preview ? null : readCachedThemeKey();

    // Render immediately from the last-known theme while the authoritative checks below
    // revalidate in the background, instead of blocking first paint on a network round trip for
    // a value that rarely changes.
    if (cachedThemeKey) {
      loadTheme(cachedThemeKey)
        .then((components) => {
          if (!cancelled) setState((prev) => prev ?? { themeKey: cachedThemeKey, components });
        })
        .catch(() => {
          // Cached theme key failed to load — the authoritative flow below still runs and will
          // set real state, so there's nothing to do here beyond not crashing.
        });
    }

    (async () => {
      let themeKey: ThemeKey = preview ?? cachedThemeKey ?? DEFAULT_THEME;

      const maintenanceCheck = platformService
        .getMaintenanceMode()
        .then(({ enabled }) => enabled)
        .catch(() => false); // Platform config unreachable — default to not-in-maintenance

      if (!preview) {
        try {
          const { activeTheme } = await platformService.getActiveTheme();
          if (isThemeKey(activeTheme)) {
            themeKey = activeTheme;
            writeCachedThemeKey(activeTheme);
          }
        } catch {
          // Platform config unreachable — render the default (or cached) theme rather than
          // blocking boot
        }
      }

      const enabled = await maintenanceCheck;
      if (!cancelled) setMaintenanceMode(enabled);

      try {
        const components = await loadTheme(themeKey);
        if (!cancelled) setState({ themeKey, components });
      } catch {
        // Selected theme failed to load (e.g. not yet implemented) — fall back to default
        const components = await loadTheme(DEFAULT_THEME);
        if (!cancelled) setState({ themeKey: DEFAULT_THEME, components });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!state) {
    return <LoadingSpinner fullScreen text="Loading..." />;
  }

  if (maintenanceMode && !canBypassMaintenance()) {
    return <MaintenanceModePage />;
  }

  return <ThemeContext.Provider value={state}>{children}</ThemeContext.Provider>;
}
